import { Inject, Injectable } from '@angular/core';

import {
    Category,
    CommonsConfigEnvironment,
    CONFIG_ENVIRONMENT,
    PcvizDataSource,
    PcvizFeature,
    PcvizGisCategory,
    PcvizJobSite,
    PcvizLayer,
    PcvizPoint,
    PcvizProject,
    PcvizWorkspaceDataset,
    PcvizWorkspaceLayerGroup,
    PcvizWorkspaceSelectionObjectIterator,
    PcvizWorkspaceVisitor,
} from '@mirage-projects/cw-models';

@Injectable({ providedIn: 'root' })
export class GisPcvizService {
    constructor(@Inject(CONFIG_ENVIRONMENT) private configEnvironment: CommonsConfigEnvironment) {}

    getDatasource(id: string): PcvizDataSource {
        const baseUrl = this.configEnvironment.port
            ? `${this.configEnvironment.baseURL}:${this.configEnvironment.port}`
            : this.configEnvironment.baseURL;
        const dataSource = new PointCloudViz.GIS.DataSource(baseUrl, id);
        dataSource.setCredentials(this.configEnvironment.API_CONFIG_CORE.username, this.configEnvironment.API_CONFIG_CORE.password);
        return dataSource;
    }

    getPcvizWorkspaceLayerGroup(): typeof PointCloudViz.GIS.WorkspaceLayerGroup {
        return PointCloudViz.GIS.WorkspaceLayerGroup;
    }

    createPcvizWorkspaceLayerGroup(wsDataset: PcvizWorkspaceDataset): PcvizWorkspaceLayerGroup {
        return new PointCloudViz.GIS.WorkspaceLayerGroup(wsDataset) as PcvizWorkspaceLayerGroup;
    }

    /**
     * Checks if the layer is an instance of WorkspaceLayerGroup
     * @param layer The layer to check
     */
    isInstanceOfWorkspaceLayerGroup(layer: PcvizLayer): boolean {
        return layer instanceof PointCloudViz.GIS.WorkspaceLayerGroup;
    }

    getWorkspaceCategories(): typeof PointCloudViz.GIS.WorkspaceCategories {
        return PointCloudViz.GIS.WorkspaceCategories;
    }

    /**
     * Gets the vector layer utils object
     */
    getPcvizVectorLayerUtils(): typeof PointCloudViz.GIS.VectorLayerUtils {
        return PointCloudViz.GIS.VectorLayerUtils;
    }

    /**
     * Gets the object type object
     */
    getObjectType(): typeof PointCloudViz.GIS.ObjectType {
        return PointCloudViz.GIS.ObjectType;
    }

    createProjectCore(projectId: number): PcvizProject {
        const dataSource = this.getDatasource('da.project');
        const projectDataset = new PointCloudViz.GIS.ProjectDataset(dataSource);
        const projectCore = new PointCloudViz.GIS.Project(projectDataset);
        projectCore.setId(projectId);
        return projectCore;
    }

    createJobSiteCore(jobSiteId: number): PcvizJobSite {
        const dataSource = this.getDatasource('da.site');
        const jobSiteDataset = new PointCloudViz.GIS.JobSiteDataset(dataSource);
        const jobSiteCore = new PointCloudViz.GIS.JobSite(jobSiteDataset);
        jobSiteCore.setId(jobSiteId);
        return jobSiteCore;
    }

    getGeometryType(): typeof PointCloudViz.GIS.GeometryType {
        return PointCloudViz.GIS.GeometryType;
    }

    getExpression(): typeof PointCloudViz.GIS.Expression {
        return PointCloudViz.GIS.Expression;
    }

    createFeature(): PcvizFeature {
        return new PointCloudViz.GIS.Feature();
    }

    createEnvelope(bbox1: any, bbox2: any, bbox3: any, bbox4: any, bbox5: any, bbox6: any): PcvizFeature {
        return new PointCloudViz.GIS.Envelope(bbox1, bbox2, bbox3, bbox4, bbox5, bbox6);
    }

    createPoint(x: number, y: number, z: number): PcvizPoint {
        return new PointCloudViz.GIS.Point(x, y, z);
    }

    getFeatureDefinition(): typeof PointCloudViz.GIS.FeatureDefinition {
        return PointCloudViz.GIS.FeatureDefinition;
    }

    getLayerType(): typeof PointCloudViz.GIS.LayerType {
        return PointCloudViz.GIS.LayerType;
    }

    createWorkspaceSelectionObjectIterator(workspace: any): PcvizWorkspaceSelectionObjectIterator {
        return new PointCloudViz.GIS.WorkspaceSelectionObjectIterator(workspace);
    }

    /**
     * Create a new workspace visitor
     */
    createWorkspaceVisitor(): PcvizWorkspaceVisitor {
        return new PointCloudViz.GIS.WorkspaceVisitor();
    }

    getVectorLayerUtils(): typeof PointCloudViz.GIS.VectorLayerUtils {
        return PointCloudViz.GIS.VectorLayerUtils;
    }

    getCategory(): PcvizGisCategory {
        return PointCloudViz.GIS.Category;
    }

    async getSystemCategoryListFromServer(): Promise<Category[]> {
        return PointCloudViz.GIS.Category.getSystemCategoryListFromServer();
    }

    isAnnotationCategory(categoryId: number): boolean {
        return PointCloudViz.GIS.Category.isAnnotation(categoryId);
    }

    createWorkspaceDataset(ds: PcvizDataSource): PcvizWorkspaceDataset {
        return new PointCloudViz.GIS.WorkspaceDataset(ds);
    }

    getGisUtils(): typeof PointCloudViz.GIS.Utils {
        return PointCloudViz.GIS.Utils;
    }

    getWorkspaceStationTypes(): typeof PointCloudViz.GIS.WorkspaceStationTypes {
        return PointCloudViz.GIS.WorkspaceStationTypes;
    }
}
